// 浏览器兼容版本的题库生成工具
const questionBanks = {
  '病理学': [
    {
      id: 'path-1',
      title: '细胞损伤',
      content: '关于细胞水肿的描述，错误的是',
      options: [
        'A. 细胞体积增大',
        'B. 胞质内出现颗粒',
        'C. 线粒体和内质网肿胀',
        'D. 属于不可逆性损伤'
      ],
      answer: 'D'
    },
    // 其他题目...
  ],
  // 其他学科...
};

// 获取题目集列表
export function getQuestionSets() {
  return Object.keys(questionBanks).map(subject => ({
    subject,
    questions: questionBanks[subject]
  }));
}

/**
 * 题库管理工具函数
 */

// 缓存答案解析，避免重复API调用
const answerCache = {};

/**
 * 生成缓存破坏参数
 * @returns {string} 时间戳参数
 */
const getCacheBuster = () => {
  return `?_t=${Date.now()}`;
};

/**
 * 获取缓存的答案解析
 * @param {string} bankId 题库ID
 * @param {string} questionId 题目ID
 * @returns {string|null} 缓存的解析或null
 */
export const getAnswerCache = (bankId, questionId) => {
  const key = `${bankId}_${questionId}`;
  return answerCache[key] || null;
};

/**
 * 更新答案解析缓存
 * @param {string} bankId 题库ID
 * @param {string} questionId 题目ID
 * @param {string} explanation 解析内容
 */
export const updateAnswerCache = (bankId, questionId, explanation) => {
  const key = `${bankId}_${questionId}`;
  answerCache[key] = explanation;
};

/**
 * 获取所有学科题库
 * @param {boolean} forceRefresh 是否强制刷新缓存
 * @returns {Promise<Object>} 题库索引数据
 */
export const getAllQuestionBanks = async (forceRefresh = false) => {
  try {
    // 生成强制刷新的URL参数
    const cacheBuster = forceRefresh ? getCacheBuster() : '';
    const url = `/question-banks/index.json${cacheBuster}`;
    
    console.log(`📡 正在请求题库索引: ${url}`);
    
    const fetchOptions = {
      method: 'GET',
      headers: {}
    };
    
    if (forceRefresh) {
      // 强制刷新时使用最强的缓存控制
      fetchOptions.cache = 'no-store';
      fetchOptions.headers = {
        'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0',
        'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT'
      };
    }
    
    const response = await fetch(url, fetchOptions);
    
    console.log(`📡 响应状态: ${response.status} ${response.statusText}`);
    console.log(`📡 响应类型: ${response.headers.get('content-type')}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // 检查响应类型
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.warn(`⚠️ 意外的内容类型: ${contentType}`);
      // 尝试读取响应文本以调试
      const text = await response.text();
      console.log(`📄 响应内容预览: ${text.substring(0, 200)}...`);
      throw new Error(`期望JSON格式，但收到: ${contentType}`);
    }
    
    const data = await response.json();
    
    // 如果强制刷新，清除相关缓存
    if (forceRefresh) {
      console.log('🔄 已刷新题库索引缓存');
      console.log('✅ 题库索引已刷新');
    }
    
    return data;
  } catch (error) {
    console.error('❌ 获取题库索引失败:', error);
    
    // 如果是开发环境，尝试备用路径
    if (process.env.NODE_ENV === 'development') {
      try {
        console.log('🔄 尝试备用路径...');
        const backupUrl = `${window.location.origin}/question-banks/index.json${forceRefresh ? getCacheBuster() : ''}`;
        const backupResponse = await fetch(backupUrl, forceRefresh ? {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        } : {});
        
        if (backupResponse.ok) {
          const data = await backupResponse.json();
          console.log('✅ 备用路径成功');
          return data;
        }
      } catch (backupError) {
        console.error('❌ 备用路径也失败:', backupError);
      }
    }
    
    return { subjects: {} };
  }
};

/**
 * 获取单个题库数据
 * @param {string} path 题库路径
 * @param {boolean} forceRefresh 是否强制刷新缓存
 * @returns {Promise<Object>} 题库数据
 */
export const getQuestionBank = async (path, forceRefresh = false) => {
  try {
    // 移除path开头的斜杠，获取相对路径
    const bankPath = path.startsWith('/') ? path.substring(1) : path;
    const cacheBuster = forceRefresh ? getCacheBuster() : '';
    const url = `/question-banks/${bankPath}${cacheBuster}`;
    
    const fetchOptions = {
      method: 'GET',
      headers: {}
    };
    
    if (forceRefresh) {
      // 强制刷新时使用最强的缓存控制
      fetchOptions.cache = 'no-store';
      fetchOptions.headers = {
        'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0',
        'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT'
      };
    }
    
    const response = await fetch(url, fetchOptions);
    
    const data = await response.json();
    
    if (forceRefresh) {
      console.log(`🔄 已刷新题库缓存: ${bankPath}`);
    }
    
    return data;
  } catch (error) {
    console.error(`获取题库数据失败 (${path}):`, error);
    return null;
  }
};

/**
 * 清除所有缓存的解析
 */
export const clearCache = () => {
  Object.keys(answerCache).forEach(key => {
    delete answerCache[key];
  });
  console.log('🧹 已清除所有答案解析缓存');
};

/**
 * 强制刷新所有题库数据
 */
export const refreshAllData = async () => {
  console.log('🔄 开始刷新所有题库数据...');
  clearCache();
  
  try {
    // 刷新索引数据
    const indexData = await getAllQuestionBanks(true);
    console.log('✅ 题库索引已刷新');
    return indexData;
  } catch (error) {
    console.error('❌ 刷新题库数据失败:', error);
    throw error;
  }
};

// 初始化题库数据
export function initializeQuestionBanks() {
  // 浏览器环境无需文件操作
  console.log('题库数据初始化完成');
}